import type { SiteID } from './SiteID';
import { AppID, siteIDToAppID } from './AppID';

/**
 * Represents a Deckers Brands brand. It typically refers to the
 * how the application is recognized by external resources.
 *
 * For instance, `UGG-COLLAB_US` is a site, but it would still have a brand of `UGG`.
 * The site ID would be used to access configuration, but the brand would be used to access
 * external resources related to that brand, e.g. orders, products, etc..
 */
export enum Brand {
  /**
   * @deprecated Will be removed in HP-4549.
   *
   * Represents the Sanuk brand.
   * @see https://www.sanuk.com/
   */
  Sanuk = 'SANUK',
  /**
   * Represents the Teva brand.
   * @see https://www.teva.com/
   */
  Teva = 'TEVA',
  /**
   * Represents the UGG brand.
   * @see https://www.ugg.com/
   */
  UGG = 'UGG',
  /**
   * Represents the Hoka brand.
   * @see https://www.hoka.com/
   */
  Hoka = 'HOKA',
  /**
   * @deprecated Will be removed in HP-4549.
   *
   * Represents the Koolaburra brand.
   * @see https://www.koolaburra.com/
   */
  Koolaburra = 'KOOLABURRA',
  /**
   * Represents the Ahnu brand.
   * @see https://www.ahnu.com/
   */
  Ahnu = 'AHNU',
  /**
   * @deprecated Will be removed in HP-4549.
   *
   * Represents the Deckers X Lab brand.
   * @see https://deckersxlab.com/
   *
   * **IMPORTANT:** We do NOT manage this site. This value is only included for
   * parity with AWS, and should not be present in any real use case.
   */
  DeckersXLab = 'DECKERSX',
  /** Represents something that is not associated with a brand. */
  None = 'NONE'
}

const APP_ID_TO_BRAND_RECORD = {
  [AppID.AHNU]: Brand.Ahnu,
  [AppID.CONDUCTOR]: Brand.None,
  [AppID.HOKA]: Brand.Hoka,
  [AppID.KOOLABURRA]: Brand.Koolaburra,
  [AppID.SANUK]: Brand.Sanuk,
  [AppID.TEVA]: Brand.Teva,
  [AppID.UGG]: Brand.UGG,
  [AppID.UGG_COLLAB]: Brand.UGG
} as const satisfies Record<AppID, Brand>;

/**
 * Gets the {@link Brand} associated with a given {@link AppID}.
 * @param appID - The {@link AppID} to get the brand for.
 * @returns The corresponding {@link Brand}.
 * @throws An error if the app ID does not correspond to a known brand.
 */
export function appIDToBrand(appID: AppID): Brand {
  // Get the brand associated with the given app ID
  const result = APP_ID_TO_BRAND_RECORD[appID];

  if (result === undefined) {
    throw new Error(`Unable to determine brand for app ID "${appID}".`);
  }

  return result;
}

/**
 * Gets the {@link Brand} associated with a given {@link SiteID}.
 * @param siteID - The {@link SiteID} to get the brand for.
 * @returns The corresponding {@link Brand}.
 * @throws An error if the site ID does not correspond to a known brand.
 */
export function siteIDToBrand(siteID: SiteID): Brand {
  let result: Brand;
  try {
    const appId = siteIDToAppID(siteID);
    result = APP_ID_TO_BRAND_RECORD[appId];
  } catch (error) {
    result = undefined!;
  }

  if (result === undefined) {
    throw new Error(`Unable to determine brand for site ID "${siteID}".`);
  }

  return result;
}
